import { Component, OnInit, Inject } from "@angular/core";
import { Designer } from "modeler/designer";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { CatalogService } from "app/shared";
import { concatMap, forkJoin, map } from "rxjs";
import { ProjectHandler } from "modeler/project-handler";
import { EntityProperty, ModelHandler } from "modeler/model-handler";
import { DialogService } from "app/dialogs/services/dialog.service";
import { MaterialSelectorComponent } from "../material-selector/material-selector.component";

interface BulkReplaceElement {
  name: string;
  replaceMaterial: string;
  allProps: EntityProperty[];
  baseProps: EntityProperty[];
}

@Component({
  selector: "app-replace-materials-dialog",
  templateUrl: "./replace-materials-dialog.component.html",
  styleUrls: ["./replace-materials-dialog.component.scss"],
})
export class ReplaceMaterialsDialogComponent implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) private project: ProjectHandler,
    private catalogService: CatalogService,
    private dialog: DialogService,
    private dialogRef: MatDialogRef<ReplaceMaterialsDialogComponent>
  ) {}

  public allElements: BulkReplaceElement[] = [];
  public displayedElements: BulkReplaceElement[] = [];
  private ds: Designer;
  private filter = "";
  private ascendSort = true;

  propertyLoader = this.catalogService.newPropertyLoader(5 * 60 * 1000);

  ngOnInit() {
    this.ds = this.project.ds;

    ModelHandler.gatherProperties(this.ds.root.children, this.propertyLoader)
      .pipe(
        concatMap((res) => res),
        map((prop) => {
          const currIndex = this.allElements.findIndex(
            (item) => item.name === prop.name
          );

          if (currIndex < 0) {
            this.allElements.push({
              name: prop.name,
              replaceMaterial: "",
              allProps: [prop],
              baseProps: [prop],
            });
          } else {
            const item = this.allElements[currIndex];
            item.allProps.push(prop);
            item.baseProps.push(prop);
          }
        })
      )
      .subscribe((_) => this.updateElementsList());
  }

  private updateElementsList() {
    this.displayedElements = this.allElements
      .filter((value) => value.name.toLowerCase().includes(this.filter))
      .sort((a, b) =>
        this.ascendSort
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );
  }

  applyFilter(event: KeyboardEvent) {
    this.filter = (event.target as HTMLInputElement).value.toLocaleLowerCase();
    this.updateElementsList();
  }

  sortList() {
    this.ascendSort = !this.ascendSort;
    this.updateElementsList();
  }

  elementRowClasses(element: BulkReplaceElement) {
    const res = [];
    const prop = element.allProps[0];
    if (prop.linkedTo > 0) {
      res.push("replace-materials-dialog__list-item-child");
    }
    return res;
  }

  setNewLinkedProps(props: EntityProperty[]) {
    const linkedId = props[0].id;
    this.allElements.forEach((elem) => {
      for (let i = 0; i < elem.allProps.length; i++) {
        const currProp = elem.allProps[i];
        if (!currProp.linkedTo) {
          continue;
        }

        const newProp = props.find(
          (prop) =>
            prop.id !== currProp.id &&
            prop.name === currProp.name &&
            prop.linkedTo === currProp.linkedTo
        );
        if (newProp) {
          elem.allProps[i] = newProp;
        } else if (currProp.linkedTo === linkedId) {
          // TODO: check property availability
        }
      }
    });
  }

  checkLinkedProps(element: BulkReplaceElement) {
    element.allProps.forEach((prop) => {
      if (!prop.linkedTo) {
        const propId = prop.id;
        const newVariant = prop.variants.find(
          (item) => item.name === element.replaceMaterial
        );

        const items = prop.elements.map((propElem) => propElem.e);
        const checkProp = (mp) => {
          let prop = mp.props.find((p) => p.id === propId);
          if (prop.value === newVariant.id) {
            return false;
          }
          prop.value = newVariant.id;
        };

        ModelHandler.gatherModelProperties(
          items,
          this.propertyLoader,
          propId
        ).subscribe((res) => {
          forkJoin(
            res
              .filter(
                (mp) => checkProp(mp) !== false && mp.hasLinkedProperties()
              )
              .map((p) =>
                p.loadProperties(p.getPropertyMap(), this.propertyLoader)
              )
          ).subscribe((res) => {
            const props = ModelHandler.convertModelProperties(res);
            this.setNewLinkedProps(props);
          });
        });
      }
    });
  }

  selectMaterial(element: BulkReplaceElement) {
    const dialogRef = this.dialog.open(MaterialSelectorComponent, {
      width: "70%",
      height: "60%",
      maxWidth: "1200px",
    });
    const selector = dialogRef.componentInstance;
    const materials = ModelHandler.gatherMaterialsFromProperties(
      element.allProps
    );
    selector.materials = this.catalogService.findMaterials(materials);
    selector.select.subscribe((res) => {
      element.replaceMaterial = res.name;
      this.checkLinkedProps(element);

      dialogRef.close();
    });
  }

  close() {
    this.dialogRef.close();
  }

  clearMaterial(element: BulkReplaceElement) {
    element.replaceMaterial = "";

    const prop = element.allProps[0];
    if (prop.linkedTo) {
      return;
    }

    for (let elem of this.allElements) {
      if (elem.name === element.name) {
        continue;
      }

      let restoreProps = false;
      for (let elemProp of elem.allProps) {
        if (elemProp.linkedTo !== prop.id) {
          continue;
        }

        elem.replaceMaterial = "";
        restoreProps = true;
        break;
      }

      if (restoreProps) {
        elem.allProps = elem.baseProps.slice();
      }
    }
  }

  async replaceAll() {
    for (let elem of this.allElements) {
      if (!elem.replaceMaterial) {
        continue;
      }

      for (let prop of elem.allProps) {
        const newVariant = prop.variants.find(
          (item) => item.name === elem.replaceMaterial
        );
        if (!newVariant) {
          continue;
        }

        const items = prop.elements.map((propElem) => propElem.e);
        await ModelHandler.applyProperty(
          this.ds,
          prop.id,
          newVariant.id,
          items,
          this.propertyLoader
        ).toPromise();
      }
    }

    this.close();
  }
}
